"use client";

import { type ReactNode, useState } from "react";
import {
  Checkbox,
  ErrorNotice,
  Notice,
  PillGroup,
  SelectField,
  TextField,
} from "@/components/tool-kit/primitives";
import { WIDGET_H2 } from "@/components/tool-kit/styles";
import { PercentAnswer } from "@/components/tools/chemistry/percent-answer";
import { QuantityInput } from "@/components/tools/chemistry/quantity-input";
import {
  SUBSTANCE_STRINGS,
  SubstanceFields,
  SubstanceProblem,
} from "@/components/tools/chemistry/substance-fields";
import {
  useToolLocale,
  useToolNumber,
  useToolUi,
} from "@/components/tools/tool-ui";
import { t, tPlural } from "@/lib/i18n/interpolate";
import { fewestSigFigs } from "@/lib/tool-kit/chemistry/format";
import {
  type PercentResult,
  type PercentSolve,
  solvePercent,
} from "@/lib/tool-kit/chemistry/percent";
import type { PercentKind } from "@/lib/tool-kit/chemistry/solutions";
import {
  resolveSubstance,
  type Substance,
  type SubstanceMode,
} from "@/lib/tool-kit/chemistry/substance";
import {
  MASS,
  toBase,
  unitOptions,
  VOLUME,
} from "@/lib/tool-kit/chemistry/units";
import { parseMeasuredNumber } from "@/lib/tool-kit/math/number-input";

/** English strings. A translated content file overrides these through `ui`. */
export const PERCENT_SOLUTION_CALCULATOR_STRINGS = {
  ...SUBSTANCE_STRINGS,
  kindLabel: "Type of percent solution",
  kindWv: "% w/v (g per 100 mL)",
  kindWw: "% w/w (g per 100 g)",
  kindVv: "% v/v (mL per 100 mL)",
  solveLabel: "Solve for",
  solveSolute: "Amount of solute",
  solvePercent: "Percent",
  solveSolution: "Total amount of solution",
  percentLabel: "Percent (%)",
  soluteLabel: "Amount of solute",
  solutionLabel: "Amount of solution",
  answerSuffix: "{label} (answer)",
  unit: "Unit",
  sigFigs: "Significant figures in the answer",
  sigMatch: "Match my data",
  sigChoice:
    "one: {count} significant figure|other: {count} significant figures",
  convertToMolarity: "Also convert to molarity",
  solutionDensity: "Density of the solution (g/mL)",
  soluteDensity: "Density of the pure solute (g/mL)",
  densityHint:
    "Needed because a mass or volume percent says nothing about moles on its own.",
  results: "Results",
  answerPercent: "Percent",
  answerSolute: "Amount of solute",
  answerSolution: "Amount of solution",
  molarity: "Molarity",
  suffixWv: "w/v",
  suffixWw: "w/w",
  suffixVv: "v/v",
  working: "The working",
  formulaWv: "% w/v = (g of solute ÷ mL of solution) × 100",
  formulaWw: "% w/w = (g of solute ÷ g of solution) × 100",
  formulaVv: "% v/v = (mL of solute ÷ mL of solution) × 100",
  substituted:
    "{percent}% = ({solute} {soluteUnit} ÷ {solution} {solutionUnit}) × 100",
  gramsPerLitreLine: "Solute per litre of solution: {expression} = {grams} g/L",
  molarityLine: "Molarity = {grams} g/L ÷ {molar} g/mol = {result} mol/L",
  needSolutionDensity:
    "Enter the solution's density to convert a % w/w solution to molarity.",
  needSoluteDensity:
    "Enter the pure solute's density to convert a % v/v solution to molarity.",
  recipeHeading: "How to make it",
  recipeWv:
    "Dissolve {solute} of solute in less than {solution} of solvent, then add solvent up to a total volume of {solution}.",
  recipeWw:
    "Weigh {solute} of solute and {solvent} of solvent, for {solution} of solution in all.",
  recipeVv:
    "Measure {solute} of the liquid solute and add solvent up to {solution}. Volumes of some liquids are not additive, so make up to the mark in a graduated flask rather than adding {solvent} of solvent.",
  enterValues: "Enter the other two quantities to see the answer.",
  invalid: "Each quantity must be a number greater than zero.",
  soluteExceeds:
    "The amount of solute is larger than the amount of solution, which would be a percent above 100.",
  copy: "Copy answer",
  copied: "Copied!",
  copyText: "{percent}: {solute} of solute in {solution} of solution",
} as const;

type Strings = typeof PERCENT_SOLUTION_CALCULATOR_STRINGS;

const SIG_CHOICES = ["3", "4", "5", "6"] as const;
const DEFAULT_SIG = 4;
const MASS_UNITS = unitOptions(MASS).filter((u) =>
  ["mg", "g", "kg"].includes(u.value)
);
const WEIGHT_SOLUTION_UNITS = unitOptions(MASS).filter((u) =>
  ["g", "kg"].includes(u.value)
);
const VOLUME_UNITS = unitOptions(VOLUME).filter((u) =>
  ["uL", "mL", "L"].includes(u.value)
);
const ML_PER_L = 1000;
const START_UNITS = {
  wv: { solute: "g", solution: "mL" },
  ww: { solute: "g", solution: "g" },
  vv: { solute: "mL", solution: "mL" },
} as const;

function unitMenus(kind: PercentKind) {
  return {
    solute: kind === "vv" ? VOLUME_UNITS : MASS_UNITS,
    solution: kind === "ww" ? WEIGHT_SOLUTION_UNITS : VOLUME_UNITS,
  };
}

/** A typed amount in the kind's base unit (g or mL); null if blank or unreadable. */
function baseAmount(
  text: string,
  kind: PercentKind,
  part: "solute" | "solution",
  unit: string
): number | null {
  const value = parseMeasuredNumber(text);
  if (text.trim() === "" || value === null) {
    return null;
  }
  const isMass = part === "solute" ? kind !== "vv" : kind === "ww";
  return isMass
    ? toBase(value, MASS, unit)
    : toBase(value, VOLUME, unit) * ML_PER_L;
}

function ResultPanel({
  entries,
  result,
  kind,
  solve,
  units,
  sig,
  withMolarity,
  substance,
  density,
  strings,
}: {
  entries: readonly string[];
  result: PercentResult | null;
  kind: PercentKind;
  solve: PercentSolve;
  units: { solute: string; solution: string };
  sig: number;
  withMolarity: boolean;
  substance: Substance;
  density: number | null;
  strings: Strings;
}): ReactNode {
  if (entries.some((text) => text.trim() === "")) {
    return <Notice>{strings.enterValues}</Notice>;
  }
  if (result === null) {
    return <ErrorNotice>{strings.invalid}</ErrorNotice>;
  }
  if (kind !== "wv" && result.percent > 100) {
    return <ErrorNotice>{strings.soluteExceeds}</ErrorNotice>;
  }
  if (withMolarity && substance.kind !== "ok") {
    return <SubstanceProblem strings={strings} substance={substance} />;
  }
  return (
    <PercentAnswer
      kind={kind}
      molarity={
        withMolarity && substance.kind === "ok"
          ? { molarMass: substance.molarMass, density }
          : null
      }
      r={result}
      sig={sig}
      solve={solve}
      strings={strings}
      units={units}
    />
  );
}

export default function PercentSolutionCalculatorWidget() {
  const strings = useToolUi(PERCENT_SOLUTION_CALCULATOR_STRINGS);
  const locale = useToolLocale();
  const fmt = useToolNumber();
  const [kind, setKind] = useState<PercentKind>("wv");
  const [solve, setSolve] = useState<PercentSolve>("solute");
  const [percentText, setPercentText] = useState("0.90");
  const [soluteText, setSoluteText] = useState("");
  const [solutionText, setSolutionText] = useState("500.0");
  const [units, setUnits] = useState<{ solute: string; solution: string }>(
    START_UNITS.wv
  );
  const [sigChoice, setSigChoice] = useState("match");
  const [withMolarity, setWithMolarity] = useState(true);
  const [mode, setMode] = useState<SubstanceMode>("formula");
  const [formula, setFormula] = useState("NaCl");
  const [molarMassText, setMolarMassText] = useState("");
  const [densityText, setDensityText] = useState("");

  const menus = unitMenus(kind);
  const changeKind = (next: PercentKind) => {
    setKind(next);
    setUnits(START_UNITS[next]);
  };

  const percent = parseMeasuredNumber(percentText);
  const entries = [
    ...(solve === "percent" ? [] : [percentText]),
    ...(solve === "solute" ? [] : [soluteText]),
    ...(solve === "solution" ? [] : [solutionText]),
  ];
  const sig =
    sigChoice === "match"
      ? fewestSigFigs(entries, DEFAULT_SIG)
      : Number(sigChoice);
  const result = solvePercent(kind, {
    solve,
    percent: percentText.trim() === "" ? null : percent,
    solute: baseAmount(soluteText, kind, "solute", units.solute),
    solution: baseAmount(solutionText, kind, "solution", units.solution),
  });
  const substance = resolveSubstance(mode, formula, molarMassText);
  const density = parseMeasuredNumber(densityText);

  const body = (
    <ResultPanel
      density={density !== null && density > 0 ? density : null}
      entries={entries}
      kind={kind}
      result={result}
      sig={sig}
      solve={solve}
      strings={strings}
      substance={substance}
      units={units}
      withMolarity={withMolarity}
    />
  );

  return (
    <>
      <PillGroup
        label={strings.kindLabel}
        name="ps-kind"
        onChange={changeKind}
        options={[
          { value: "wv", label: strings.kindWv },
          { value: "ww", label: strings.kindWw },
          { value: "vv", label: strings.kindVv },
        ]}
        value={kind}
      />
      <PillGroup
        label={strings.solveLabel}
        name="ps-solve"
        onChange={setSolve}
        options={[
          { value: "solute", label: strings.solveSolute },
          { value: "percent", label: strings.solvePercent },
          { value: "solution", label: strings.solveSolution },
        ]}
        value={solve}
      />
      <div className="grid gap-4 sm:grid-cols-2">
        {solve === "percent" ? (
          <p className="text-muted-foreground text-sm sm:col-span-2">
            {t(strings.answerSuffix, { label: strings.percentLabel })}
          </p>
        ) : (
          <TextField
            id="ps-percent"
            label={strings.percentLabel}
            numeric
            onChange={setPercentText}
            value={percentText}
          />
        )}
        <QuantityInput
          id="ps-solute"
          label={
            solve === "solute"
              ? t(strings.answerSuffix, { label: strings.soluteLabel })
              : strings.soluteLabel
          }
          onUnitChange={(solute) => setUnits({ ...units, solute })}
          onValueChange={setSoluteText}
          solved={solve === "solute"}
          unit={units.solute}
          unitLabel={strings.unit}
          units={menus.solute}
          value={soluteText}
        />
        <QuantityInput
          id="ps-solution"
          label={
            solve === "solution"
              ? t(strings.answerSuffix, { label: strings.solutionLabel })
              : strings.solutionLabel
          }
          onUnitChange={(solution) => setUnits({ ...units, solution })}
          onValueChange={setSolutionText}
          solved={solve === "solution"}
          unit={units.solution}
          unitLabel={strings.unit}
          units={menus.solution}
          value={solutionText}
        />
        <SelectField
          id="ps-sig"
          label={strings.sigFigs}
          onChange={setSigChoice}
          options={[
            { value: "match", label: strings.sigMatch },
            ...SIG_CHOICES.map((value) => ({
              value,
              label: tPlural(strings.sigChoice, locale, Number(value), {
                count: fmt(Number(value)),
              }).trim(),
            })),
          ]}
          value={sigChoice}
        />
      </div>
      <Checkbox
        checked={withMolarity}
        label={strings.convertToMolarity}
        onChange={setWithMolarity}
      />
      {withMolarity ? (
        <div className="space-y-4">
          <SubstanceFields
            formula={formula}
            formulaPlaceholder="NaCl"
            id="ps"
            mode={mode}
            molarMass={molarMassText}
            molarMassPlaceholder="58.44"
            onFormulaChange={setFormula}
            onModeChange={setMode}
            onMolarMassChange={setMolarMassText}
            strings={strings}
          />
          {kind === "wv" ? null : (
            <TextField
              hint={strings.densityHint}
              id="ps-density"
              label={
                kind === "ww" ? strings.solutionDensity : strings.soluteDensity
              }
              numeric
              onChange={setDensityText}
              value={densityText}
            />
          )}
        </div>
      ) : null}
      <section aria-live="polite" className="space-y-4">
        <h2 className={WIDGET_H2}>{strings.results}</h2>
        {body}
      </section>
    </>
  );
}
